import { useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useUser } from 'context/user/User';
import userApi from 'api/requests/user';

/**
 * Fluxo pós-autenticação compartilhado pelos logins sociais (Google e Apple),
 * usado tanto na tela /entrar quanto no LoginModal.
 *
 * Os dois provedores terminam igual: a API devolve o mesmo corpo do login normal,
 * e conta recém-criada (`isNewUser`) passa por completar o nome e escolher as
 * preferências antes de seguir para o destino.
 *
 * @param {string|null} redirectTo - para onde navegar ao final. `null` apenas recarrega.
 */
export default function useSocialLogin(redirectTo) {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { user, getUser } = useUser();

    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [showCompleteName, setShowCompleteName] = useState(false);
    const [showPreferences, setShowPreferences] = useState(false);

    const finish = useCallback(() => {
        if (redirectTo) {
            searchParams.delete('redirectTo');
            navigate({
                pathname: redirectTo,
                search: new URLSearchParams(Object.fromEntries(searchParams)).toString()
            }, { replace: true });
        }

        window.location.reload();
    }, [navigate, redirectTo, searchParams]);

    /**
     * @param {() => Promise} request - a chamada do provedor, ex. () => loginApi.withApple(payload)
     * @param {string} fallbackMessage - mensagem de erro quando a API não mandar uma
     * @returns {Promise<boolean>} se a chamada ao provedor foi aceita pela API
     */
    const authenticate = useCallback(async (request, fallbackMessage) => {
        setErrorMessage('');
        setLoading(true);

        try {
            const { data } = await request();
            localStorage.setItem('token', data.accessToken);

            if (!data.isNewUser) {
                finish();
                return true;
            }

            const fetchedUser = await getUser();

            if (!fetchedUser) {
                setErrorMessage('Erro ao carregar dados do usuário. Tente novamente.');
                return true;
            }

            if (fetchedUser.profile_completed === false) {
                setShowCompleteName(true);
            } else {
                setShowPreferences(true);
            }

            return true;
        } catch (error) {
            setErrorMessage(error?.response?.data?.message || fallbackMessage);
            return false;
        } finally {
            setLoading(false);
        }
    }, [finish, getUser]);

    // O LoginModal reaproveita a mesma instância entre aberturas: reabrir tem que
    // voltar para a escolha de método, não para o passo de onboarding de antes.
    const reset = useCallback(() => {
        setErrorMessage('');
        setShowCompleteName(false);
        setShowPreferences(false);
    }, []);

    const completeName = useCallback(async ({ first_name, last_name }) => {
        await userApi.update(user.id, { first_name, last_name });
        setShowCompleteName(false);
        setShowPreferences(true);
    }, [user]);

    const completeTags = useCallback(async (tagIds) => {
        try {
            await userApi.update(user.id, { tags: tagIds });
            finish();
        } catch (error) {
            setErrorMessage(error?.response?.data?.message || 'Erro ao salvar preferências');
        }
    }, [finish, user]);

    return {
        loading,
        errorMessage,
        setErrorMessage,
        showCompleteName,
        showPreferences,
        authenticate,
        reset,
        completeName,
        completeTags
    };
}
